import React, { useCallback, useEffect, useState } from 'react';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { fetchSettingsPortal, atualizarSettingPortal } from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Aba "Formas de pagamento" da configuração de Cotações (at-10).
 *
 * Nasceu como a lista que o fornecedor marcava no link (pedido da Alessandra, 21/08/2026).
 * 🔴 Desde a portal-cotacoes/at-16 (reunião de Compras de 28/10/2026) o fornecedor NÃO marca
 * mais nada: a forma de pagamento é combinada com ele e escolhida pelo COMPRADOR ao gerar o
 * pedido. A lista continua sendo editada aqui, e é dela que sai o select do "Gerar pedidos".
 *
 * ⚠️ Lista vazia impediria gerar qualquer pedido, então o backend a recusa e a tela também.
 *
 * Estilo: nada de CSS próprio — card, hint e botões vêm de `adminSistema.css` e a tabela é a do
 * padrão do sistema (`Tabela*`, importada pela página hospedeira), como nas outras abas.
 */

const CHAVE = 'FORMAS_PAGAMENTO_OPCOES';
const MAX_CARACTERES = 80;

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta lista faz',
    conteudo: (
      <p>
        São as opções que o <strong>comprador</strong> escolhe ao gerar o pedido de cada
        fornecedor. O fornecedor não marca forma de pagamento no link: ela é combinada com ele, e
        o que ele escrever sobre pagamento aparece na proposta.
      </p>
    ),
  },
  {
    titulo: 'Editar não muda proposta já enviada',
    conteudo: (
      <p>
        Renomear ou remover uma opção aqui vale <strong>daqui em diante</strong>. Propostas
        antigas, de quando o fornecedor ainda marcava a forma no link, continuam mostrando o que
        ele marcou no dia.
      </p>
    ),
  },
  {
    titulo: 'A lista não pode ficar vazia',
    conteudo: (
      <p>
        Sem nenhuma opção, <strong>nenhum pedido poderia ser gerado</strong>, porque todo pedido
        enviado ao Nasajon leva uma forma de pagamento. Por isso salvar uma lista vazia é
        recusado.
      </p>
    ),
  },
];

interface FormasPagamentoSectionProps {
  /**
   * Só ADMIN/TI gravam configuração no Portal (`PUT /settings/:key`). Oferecer os controles a
   * quem não pode gravar daria um caminho que só devolve 403 — o mesmo erro de UX que o gating
   * de TI existe para evitar (ADR 0012). A imposição real é do backend; isto é a UI honesta.
   */
  podeEditar: boolean;
}

const FormasPagamentoSection: React.FC<FormasPagamentoSectionProps> = ({ podeEditar }) => {
  const [opcoes, setOpcoes] = useState<string[]>([]);
  const [nova, setNova] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const settings = await fetchSettingsPortal();
      const bruto = settings[CHAVE];
      const lista = bruto ? JSON.parse(bruto) : [];
      setOpcoes(Array.isArray(lista) ? lista.filter((f: unknown) => typeof f === 'string') : []);
      setErro(false);
    } catch (error) {
      // Lista vazia por erro seria lida como "não há formas configuradas" — uma afirmação de
      // negócio derivada de falha de API (regra da at-60). O bloco diz que não conseguiu ler.
      setOpcoes([]);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar as formas de pagamento. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar(lista: string[], mensagem: string) {
    setSalvando(true);
    try {
      await atualizarSettingPortal(CHAVE, JSON.stringify(lista));
      setOpcoes(lista);
      notify.success(mensagem);
    } catch (error) {
      // Nunca dar o salvamento como feito: o estado da tela permanece o anterior.
      notify.errorFrom(error, 'Não foi possível salvar as formas de pagamento.');
    } finally {
      setSalvando(false);
    }
  }

  function adicionar() {
    const limpa = nova.trim();
    if (!limpa) return;
    if (limpa.length > MAX_CARACTERES) {
      notify.warning('Cada forma de pagamento pode ter no máximo ' + MAX_CARACTERES + ' caracteres.');
      return;
    }
    if (opcoes.some((o) => o.toLowerCase() === limpa.toLowerCase())) {
      notify.warning('Esta forma de pagamento já está na lista.');
      return;
    }
    setNova('');
    salvar([...opcoes, limpa], 'Forma de pagamento adicionada com sucesso.');
  }

  function remover(opcao: string) {
    if (opcoes.length === 1) {
      // O backend recusaria de qualquer forma; barrar aqui é o único ponto em que a explicação
      // ainda está junto de quem pode decidir o que fazer.
      notify.warning(
        'A lista não pode ficar vazia: sem nenhuma forma de pagamento, nenhum pedido poderia ser gerado.',
      );
      return;
    }
    salvar(
      opcoes.filter((o) => o !== opcao),
      'Forma de pagamento removida com sucesso.',
    );
  }

  return (
    <>
      <div className="aviso-de-tela">
        O <strong>comprador</strong> escolhe uma destas opções ao gerar o pedido de cada
        fornecedor; o fornecedor não as vê no link. Editar a lista vale <strong>daqui em
        diante</strong>.
      </div>

      <div className="admin-card">
        <div className="admin-card-title">
          Formas de pagamento dos pedidos
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setAjudaAberta(true)}
          >
            ?
          </button>
        </div>

        <p className="admin-hint" style={{ marginTop: 0 }}>
          {podeEditar
            ? 'São as opções que o comprador escolhe ao gerar o pedido; ao menos uma precisa estar cadastrada.'
            : 'São as opções que o comprador escolhe ao gerar o pedido. A lista é mantida por um administrador ou pela TI.'}
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar as formas de pagamento, então não dá para saber o que está
            cadastrado hoje. Recarregue a página antes de editar; se persistir, acione a
            TI.
          </p>
        ) : carregando ? (
          <p className="admin-hint">Carregando...</p>
        ) : (
          <>
            {opcoes.length === 0 && (
              <p className="admin-hint admin-hint-alerta">
                Nenhuma forma de pagamento cadastrada — enquanto a lista estiver vazia,{' '}
                <strong>nenhum pedido pode ser gerado</strong>.
              </p>
            )}

            <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: podeEditar ? '3fr 1fr' : '1fr',
                  width: '100%',
                  minWidth: '420px',
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      <div className="Opcoes">Forma de pagamento</div>
                      {podeEditar && <div className="Opcoes">Ações</div>}
                    </div>
                  </div>
                </div>

                {opcoes.map((opcao) => (
                  <div key={opcao} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                    <div className="Item">
                      <div className="Data" title={opcao} style={{ fontWeight: 600 }}>
                        {opcao}
                      </div>
                    </div>
                    {podeEditar && (
                      <div className="Item">
                        <button
                          type="button"
                          className="admin-btn admin-btn-secondary"
                          disabled={salvando}
                          onClick={() => remover(opcao)}
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {podeEditar && (
              <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                <input
                  className="admin-input"
                  placeholder="Ex.: Boleto 60 dias"
                  maxLength={MAX_CARACTERES}
                  value={nova}
                  disabled={salvando}
                  onChange={(e) => setNova(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') adicionar();
                  }}
                />
                <button
                  type="button"
                  className="admin-btn admin-btn-primary"
                  disabled={salvando || !nova.trim()}
                  onClick={adicionar}
                >
                  Adicionar
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Formas de pagamento"
        secoes={helpSections}
      />
    </>
  );
};

export default FormasPagamentoSection;
